import {yearSelect} from '../../../utils/FormUtils/yearSelect';

import benchmark from './benchmark';

export interface YearOnYearScopeEmissions {
  year: string;
  scope1: number;
  scope2: number;
  scope3: number;
}

const SCOPE_DATA_IDS = {
  scope1: 'Scope 1 GHG emissions',
  scope2: 'Scope 2 GHG emissions',
  scope3: 'Scope 3 GHG emissions'
} as const;

const BATCH_SIZE = 5;

// Caps how far back the year-on-year chart looks. Without this, the loop
// below would attempt every financial year since 2000 (~26 requests) before
// its empty-batch break could kick in. 10 years covers typical ESG/carbon
// reporting horizons; adjust here only, this does not affect yearSelect()'s
// other platform-wide uses (year dropdowns, etc.).
const YEAR_LOOKBACK_LIMIT = 10;

const toNumber = (value: unknown): number => {
  const parsed = Number(value);

  return Number.isFinite(parsed) ? parsed : 0;
};

interface ScopeWiseRecord {
  dataId?: string;
  value?: number | string;
}

const toScopeRow = (
  year: string,
  rows: ScopeWiseRecord[] | undefined
): YearOnYearScopeEmissions | null => {
  if (!rows?.length) {
    return null;
  }

  const valueOf = (dataId: string): number =>
    toNumber(rows.find((item) => item?.dataId === dataId)?.value);

  const row = {
    year,
    scope1: valueOf(SCOPE_DATA_IDS.scope1),
    scope2: valueOf(SCOPE_DATA_IDS.scope2),
    scope3: valueOf(SCOPE_DATA_IDS.scope3)
  };

  // The endpoint returns zero-filled rows for years that were never reported,
  // so a year only counts as filled when at least one scope has a value.
  return row.scope1 || row.scope2 || row.scope3 ? row : null;
};

export const yearScopeEmissions = async (
  benchmarkDataId: string,
  year: string
): Promise<YearOnYearScopeEmissions | null> => {
  if (!year) {
    return null;
  }

  const response = await benchmark(benchmarkDataId, year);

  return toScopeRow(year, response?.data);
};

const yearOnYearEmissions = async (
  benchmarkDataId: string
): Promise<YearOnYearScopeEmissions[]> => {
  // yearSelect() is the platform-wide year list (newest first) and already
  // carries the company's financial-year format, e.g. '2025-2026'.
  const years = yearSelect()
    .map((option) => option.value)
    .slice(0, YEAR_LOOKBACK_LIMIT);
  const collected: YearOnYearScopeEmissions[] = [];

  for (let index = 0; index < years.length; index += BATCH_SIZE) {
    const batch = years.slice(index, index + BATCH_SIZE);

    // eslint-disable-next-line no-await-in-loop
    const responses = await Promise.all(
      batch.map(async (year) => {
        try {
          const response = await benchmark(benchmarkDataId, year);

          return toScopeRow(year, response?.data);
        } catch {
          return null;
        }
      })
    );

    const filled = responses.filter(
      (row): row is YearOnYearScopeEmissions => row !== null
    );

    if (!filled.length) {
      break;
    }

    collected.push(...filled);
  }

  return collected.reverse();
};

export default yearOnYearEmissions;
